var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ContainerControl } from './ContainerControl';
import AccountsComboBoxTemplate from '@front/templates/AccountsComboBoxTemplate.html';
import { AccountMenuItemsHelper } from '@shared/commons/AccountWidget/AccountMenuItemsHelper';
import { Resources } from '@shared/localizations/Resources';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { AccountUtils } from '@shared/commons/cache/AccountUtils';
import { Rectangle } from '@shared/commons/Geometry';
import { MenuIcons } from '@shared/utils/Enums/MenuIcons';
export class AccountsComboBox extends ContainerControl {
    get isShowDropdownMenu() {
        return this.get('isShowDropdownMenu');
    }
    set isShowDropdownMenu(value) {
        void this.set('isShowDropdownMenu', value);
    }
    constructor() {
        super();
        this.lostFocusOnOutsideClick = true;
    }
    getType() {
        return 'AccountsComboBox';
    }
    oninit() {
        this.observe('selectedItem', this.onSelectedItemChanged, { init: false });
        this.observe('isShowDropdownMenu', this.onDropdownMenuVisibleChanged, { init: false });
    }
    oncomplete() {
        super.oncomplete();
        void this.populateAsync().then(() => {
            this.observe('excludeAccounts', this.onExcludeAccountsChanged);
        });
    }
    onteardown() {
        if (this.isShowDropdownMenu) {
            hideContextMenu();
        }
        super.onteardown();
    }
    onComboBoxClick(context) {
        if (this.isShowDropdownMenu) {
            this.isShowDropdownMenu = false;
        }
        else {
            this.isShowDropdownMenu = true;
        }
    }
    getContextMenuItem() {
        return {
            type: NewContextMenuItemType.Default,
            text: 'accountsLookup.ContextMenuItem',
            icon: MenuIcons.AccountLookup,
            event: () => {
                var _a;
                const accLookup = (_a = this.parent) === null || _a === void 0 ? void 0 : _a.getLookup();
                if (accLookup != null) {
                    accLookup.showForm(this.getX(), this.getY());
                }
            }
        };
    }
    selectItem(item) {
        const isMultiSelect = this.get('isMultiSelect');
        if (isMultiSelect) {
            let selectedItems = this.get('selectedItems');
            const isExist = selectedItems.Contains(item.value);
            if (item.checked && !isExist) {
                selectedItems.push(item.value);
            }
            else if (!item.checked && isExist) {
                selectedItems = selectedItems.filter((acc) => acc !== item.value);
            }
            this.set('selectedItems', selectedItems);
        }
        else {
            this.set('selectedItem', item.value);
        }
    }
    onExcludeAccountsChanged(n, o) {
        void this.populateAsync(); // Re-populate the menu items to apply the new excludeAccounts filter
    }
    onSelectedItemChanged(newValue) {
        const menuItems = this.get('accMenuItems');
        for (let i = 0; i < menuItems.length; i++) {
            const menuItem = menuItems[i];
            menuItem.selected = menuItem.value === newValue;
        }
    }
    onDropdownMenuVisibleChanged(isVisible) {
        var _a;
        if (isVisible) {
            const accMenuItems = (_a = this.get('accMenuItems')) !== null && _a !== void 0 ? _a : [];
            const rect = this.find('.js-accountsComboBox').getBoundingClientRect();
            showContextMenu({
                x: rect.x,
                y: rect.bottom,
                items: [...accMenuItems],
                functions: {
                    closeCallback: () => {
                        this.isShowDropdownMenu = false;
                    },
                    onOutsideClick: (event) => {
                        const elementRect = new Rectangle(rect.x, rect.y, rect.width, rect.height);
                        if (!elementRect.Contains(event.x, event.y)) {
                            this.isShowDropdownMenu = false;
                        }
                    }
                }
            });
        }
        else {
            hideContextMenu();
        }
    }
    populateAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const selectedAccounts = this.get('isMultiSelect') === true ? this.get('selectedItems') : [this.get('selectedItem')];
            const Data = AccountMenuItemsHelper.GetDataToFillOut(selectedAccounts, this.get('isMultiSelect'));
            const excludeAccounts = this.get('excludeAccounts');
            // Filter out items with accounts in excludeAccounts
            const filteredItems = isValidArray(excludeAccounts)
                ? Data.Items.filter((item) => !excludeAccounts.some((excludeAcc) => excludeAcc === item.value))
                : Data.Items;
            for (let i = 0; i < filteredItems.length; i++) {
                filteredItems[i].event = (item) => {
                    this.selectItem(item);
                };
            }
            yield this.set({
                accMenuItems: filteredItems,
                onlyOwns: Data.IsOnlyOwnAccounts,
                addtype: Data.IsAddType
            });
        });
    }
}
ContainerControl.extendWith(AccountsComboBox, {
    template: AccountsComboBoxTemplate,
    data: function () {
        return {
            dropdownLeft: 0,
            dropdownTop: 0,
            excludeAccounts: [], // Account[] - those accounts will NOT be in the drop-down list
            isPosAbsolute: false,
            addtype: true,
            selectedItem: null,
            accMenuItems: [],
            isMultiSelect: false,
            selectedItems: [],
            menuWidth: 200,
            isShowDropdownMenu: false
        };
    },
    computed: {
        selectedItemMode: {
            get: function () {
                const selectedItem = this.get('selectedItem');
                if (isNullOrUndefined(selectedItem)) {
                    return '';
                }
                return AccountUtils.GetAccountModeText(selectedItem);
            }
        },
        selectedItemText: {
            get: function () {
                const selectedItem = this.get('selectedItem');
                if (isNullOrUndefined(selectedItem)) {
                    return '';
                }
                return selectedItem.toString(true);
            }
        },
        selectedItemsText: {
            get: function () {
                const selectedItems = this.get('selectedItems');
                if (!isValidArray(selectedItems)) {
                    return Resources.getResource('noSelection');
                }
                if (selectedItems.length === 1) {
                    return selectedItems[0].toString();
                }
                const accMenuItems = this.get('accMenuItems');
                if (selectedItems.length === accMenuItems.length) {
                    return Resources.getResource('PopupMultiCkeckBox.All');
                }
                return Resources.getResource('partialSelection', [`${selectedItems.length}/${accMenuItems.length}`]);
            }
        }
    }
});
